import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  searchUniversalRecords,
  UNIVERSAL_SEARCH_HINT,
} from '../lib/universalSearch.js';

export default function HeaderSearch({
  matters = [],
  evidenceMatters = null,
  debtors = [],
  onOpenMatter,
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  const results = useMemo(
    () => searchUniversalRecords({ matters, evidenceMatters, debtors, query }),
    [matters, evidenceMatters, debtors, query]
  );

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  function closeAndClear() {
    setOpen(false);
    setQuery('');
  }

  function openHit(hit) {
    if (hit.type === 'demand' && hit.matter && typeof onOpenMatter === 'function') {
      onOpenMatter(hit.matter);
    } else if (hit.href) {
      navigate(hit.href);
    }
    closeAndClear();
  }

  return (
    <div className={`header-search${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="header-search-toggle"
        aria-label="Search records"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path
            d="M16.2 16.2 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {open ? (
        <div className="header-search-panel" role="search">
          <label className="header-search-field">
            <span className="sr-only">{UNIVERSAL_SEARCH_HINT}</span>
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search all records…"
            />
          </label>
          {query.trim() ? (
            results.total ? (
              <div className="header-search-groups">
                {results.groups.map((group) => (
                  <section key={group.id} className="header-search-group" aria-label={group.label}>
                    <p className="header-search-group-label">{group.label}</p>
                    <ul className="header-search-results">
                      {group.hits.map((hit) => (
                        <li key={`${hit.type}-${hit.id}`}>
                          <button type="button" onClick={() => openHit(hit)}>
                            <strong>{hit.title}</strong>
                            {hit.subtitle ? <span>{hit.subtitle}</span> : null}
                          </button>
                        </li>
                      ))}
                      {group.viewAllHref ? (
                        <li className="header-search-footer">
                          <Link to={group.viewAllHref} onClick={closeAndClear}>
                            View all {group.label.toLowerCase()}
                            {group.id === 'demands' ||
                            group.id === 'payments' ||
                            group.id === 'messages'
                              ? ` matching “${results.query}”`
                              : ''}
                          </Link>
                        </li>
                      ) : null}
                    </ul>
                  </section>
                ))}
              </div>
            ) : (
              <p className="header-search-empty">No records match “{query.trim()}”.</p>
            )
          ) : (
            <p className="header-search-empty">{UNIVERSAL_SEARCH_HINT}</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
